import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TxHistoryTableHeader } from './TxHistoryTableHeader';
import { TxRecord } from './TransactionRecord/TxRecord';
import { api } from '../../../../api';
import { BitcoinTransaction, BitcoinTransactionResponse } from './types';
import { TxHistoryTablePagination } from './TxHistoryTablePagination';
import { useAllAttributions } from 'src/hooks/attributions/useAllAttributions';
import { useState, useRef } from 'react';
import { BsIncognito } from 'react-icons/bs';
import './TransactionRecord/TransactionInputsOutputsTable/styles.css';
import { TxContext } from './TxContext';
import { TxHistoryTableSkeleton } from './TxHistoryTable.skeleton';

export const TxHistoryTable = () => {
  const { id: address } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
  const sortParam = searchParams.get('sort');
  const sort = sortParam === 'oldest' ? 'oldest' : 'newest'; // Default to 'newest'
  const [showAttribution, setShowAttribution] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  const {
    data: transactionsData,
    isLoading,
    error,
  } = useQuery<BitcoinTransactionResponse>({
    queryKey: ['bitcoinTransactions', address, currentPage, sort],
    queryFn: () => {
      if (!address) {
        throw new Error('Address is required');
      }
      return api.blockchain.getBitcoinAddressTransactions(
        address,
        currentPage,
        sort,
      );
    },
    enabled: !!address,
    gcTime: 0, // Disable caching as there might be a new transaction
  });

  const transactions = transactionsData?.docs ?? [];
  const totalPages = transactionsData?.totalPages ?? 0;

  // Kick off the batch attribution fetch - only when we have actual transaction data
  useAllAttributions(
    'bitcoin',
    transactions,
    address && transactions.length > 0 ? [address] : [],
  );

  if (isLoading) {
    return <TxHistoryTableSkeleton />;
  }

  if (error) {
    return <div>Error: {(error as Error).message}</div>;
  }

  if (!address) {
    return <div>No address provided</div>;
  }

  return (
    <div className="relative">
      <div
        ref={tableRef}
        className="m-28 mx-auto mb-6 mt-8 w-full max-w-[1500px] rounded-xl border border-muted-50 border-slate-200 bg-background bg-white p-6 shadow-[0_0_10px_rgba(0,0,0,0.1)] dark:border-muted-200"
      >
        <div className="mb-4">
          <TxHistoryTableHeader
            totalTransactions={transactionsData?.totalDocs ?? 0}
            currentSort={sort}
          />
        </div>

        {/* Transaction Items - no Suspense wrapper needed */}
        <div
          className={`space-y-6 ${showAttribution ? 'show-attribution' : 'hide-attribution'}`}
        >
          {transactions.map((transaction: BitcoinTransaction) => {
            return (
              <TxContext.Provider key={transaction.id} value={{ transaction }}>
                <TxRecord />
              </TxContext.Provider>
            );
          })}
        </div>

        <TxHistoryTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          currentSort={sort}
          tableRef={tableRef}
        />
      </div>

      {/* Floating Button */}
      <div className="fixed right-8 top-1/2 z-50 -translate-y-1/2 transform">
        <button
          onClick={() => setShowAttribution((prev) => !prev)}
          className="rounded-lg bg-white p-3 shadow-lg hover:bg-gray-100"
          title="Toggle attribution visibility"
        >
          <BsIncognito
            className={showAttribution ? 'text-black' : 'text-red-500'}
            size={20}
          />
        </button>
      </div>
    </div>
  );
};
